import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Link,
} from '@chakra-ui/react';
import {
  faenasApi, catalogosApi, localidadesApi, TIPOS_VIALIDAD,
  Faena, CatalogoItem, Localidad,
} from '../../../api/serviciosApi';
import { useToast } from '@chakra-ui/react';
import MapSelector from '../../../components/MapSelector';
import EvidenciasInline from '../EvidenciasInline';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: Faena;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const ESTATUS = ['Pendiente', 'Programada', 'En proceso', 'Finalizada', 'Cancelada'];
const PRIORIDADES = ['Baja', 'Media', 'Alta'];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">
      {children}
    </Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const dateOnly = (v?: string) => (v ? v.split('T')[0] : '');
const numOrUndef = (v: string) => (v === '' || v == null ? undefined : Number(v));

const FormularioFaena: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [tiposFaena, setTiposFaena] = useState<CatalogoItem[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [guardando, setGuardando] = useState(false);

  const [folio, setFolio] = useState(data?.folio || '');
  const [fecha, setFecha] = useState(dateOnly(data?.fecha) || new Date().toISOString().split('T')[0]);
  const [tipoFaenaId, setTipoFaenaId] = useState(data?.tipoFaenaId != null ? String(data.tipoFaenaId) : '');
  const [descripcion, setDescripcion] = useState(data?.descripcion || '');
  const [prioridad, setPrioridad] = useState(data?.prioridad || 'Media');
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [fechaProgramada, setFechaProgramada] = useState(dateOnly(data?.fechaProgramada));
  const [fechaEjecutada, setFechaEjecutada] = useState(dateOnly(data?.fechaEjecutada));
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [estatus, setEstatus] = useState(data?.estatus || 'Pendiente');

  const [solicitanteNombre, setSolicitanteNombre] = useState(data?.solicitanteNombre || '');
  const [solicitanteApPaterno, setSolicitanteApPaterno] = useState(data?.solicitanteApPaterno || '');
  const [solicitanteApMaterno, setSolicitanteApMaterno] = useState(data?.solicitanteApMaterno || '');
  const [solicitanteTelefono, setSolicitanteTelefono] = useState(data?.solicitanteTelefono || '');
  const [solicitanteCelular, setSolicitanteCelular] = useState(data?.solicitanteCelular || '');
  const [solicitanteCorreo, setSolicitanteCorreo] = useState(data?.solicitanteCorreo || '');

  const [estado, setEstado] = useState(data?.estado || 'Tlaxcala');
  const [municipio, setMunicipio] = useState(data?.municipio || 'Totolac');
  const [localidad, setLocalidad] = useState(data?.localidad || '');
  const [colonia, setColonia] = useState(data?.colonia || '');
  const [codigoPostal, setCodigoPostal] = useState(data?.codigoPostal || '');
  const [tipoVialidad, setTipoVialidad] = useState(data?.tipoVialidad || '');
  const [vialidad, setVialidad] = useState(data?.vialidad || '');
  const [numeroExterior, setNumeroExterior] = useState(data?.numeroExterior || '');
  const [numeroInterior, setNumeroInterior] = useState(data?.numeroInterior || '');
  const [referencias, setReferencias] = useState(data?.referencias || '');
  const [latitud, setLatitud] = useState(data?.latitud != null ? String(data.latitud) : '');
  const [longitud, setLongitud] = useState(data?.longitud != null ? String(data.longitud) : '');
  const [urlMaps, setUrlMaps] = useState(data?.urlMaps || '');

  useEffect(() => {
    catalogosApi.tiposFaena().then(setTiposFaena).catch(() => setTiposFaena([]));
    localidadesApi.list().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  const handleGuardar = async () => {
    if (!fecha) {
      toast({ title: 'Campos obligatorios', description: 'La fecha es requerida.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const body: Partial<Faena> = {
        folio: folio || undefined,
        fecha,
        tipoFaenaId: numOrUndef(tipoFaenaId),
        descripcion,
        prioridad,
        responsable,
        fechaProgramada: fechaProgramada || undefined,
        fechaEjecutada: fechaEjecutada || undefined,
        observaciones,
        estatus,
        solicitanteNombre,
        solicitanteApPaterno,
        solicitanteApMaterno,
        solicitanteTelefono,
        solicitanteCelular,
        solicitanteCorreo,
        estado,
        municipio,
        localidad,
        colonia,
        codigoPostal,
        tipoVialidad,
        vialidad,
        numeroExterior,
        numeroInterior,
        referencias,
        latitud: numOrUndef(latitud),
        longitud: numOrUndef(longitud),
        urlMaps,
      };
      if (modo === 'edit' && data) {
        await faenasApi.update(data.id, body);
      } else {
        await faenasApi.create(body);
      }
      toast({ title: modo === 'edit' ? 'Faena actualizada' : 'Faena registrada', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar la faena.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const fmt = (v?: string) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');

  // ========== MODO VISTA ==========
  if (modo === 'view') {
    const d = data || ({} as Faena);
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Información General</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="FOLIO" value={d.folio} />
          <Field label="FECHA" value={fmt(d.fecha)} />
          <Field label="TIPO DE FAENA" value={d.tipoFaena?.descripcion} />
          <Field label="PRIORIDAD" value={<Badge colorScheme={d.prioridad === 'Alta' ? 'red' : d.prioridad === 'Media' ? 'orange' : 'green'}>{d.prioridad}</Badge>} />
          <Field label="RESPONSABLE" value={d.responsable} />
          <Field label="ESTATUS" value={<Badge>{d.estatus}</Badge>} />
          <Field label="FECHA PROGRAMADA" value={fmt(d.fechaProgramada)} />
          <Field label="FECHA EJECUTADA" value={fmt(d.fechaEjecutada)} />
          <Box gridColumn={{ md: 'span 2' }}>
            <Field label="DESCRIPCIÓN" value={d.descripcion} />
          </Box>
          <Box gridColumn={{ md: 'span 2' }}>
            <Field label="OBSERVACIONES" value={d.observaciones} />
          </Box>
        </SimpleGrid>

        <SectionTitle>Solicitante</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="NOMBRE" value={[d.solicitanteNombre, d.solicitanteApPaterno, d.solicitanteApMaterno].filter(Boolean).join(' ')} />
          <Field label="TELÉFONO" value={d.solicitanteTelefono} />
          <Field label="CELULAR" value={d.solicitanteCelular} />
          <Field label="CORREO" value={d.solicitanteCorreo} />
        </SimpleGrid>

        <SectionTitle>Domicilio / Ubicación</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="ESTADO" value={d.estado} />
          <Field label="MUNICIPIO" value={d.municipio} />
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="COLONIA" value={d.colonia} />
          <Field label="C.P." value={d.codigoPostal} />
          <Field label="VIALIDAD" value={[d.tipoVialidad, d.vialidad].filter(Boolean).join(' ')} />
          <Field label="No. EXT / INT" value={[d.numeroExterior, d.numeroInterior].filter(Boolean).join(' / ')} />
          <Field label="REFERENCIAS" value={d.referencias} />
          <Field label="UBICACIÓN" value={d.latitud != null && d.longitud != null ? (
            <Link color="blue.500" isExternal href={d.urlMaps || `https://www.google.com/maps?q=${d.latitud},${d.longitud}`}>Ver en Google Maps</Link>
          ) : undefined} />
        </SimpleGrid>

        <EvidenciasInline archivos={d.archivos} />
      </VStack>
    );
  }

  // ========== NUEVO / EDITAR ==========
  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Información General</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Folio</FormLabel>
          <Input placeholder="Autogenerado si se deja vacío" value={folio} onChange={(e) => setFolio(e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de faena</FormLabel>
          <Select placeholder="Selecciona tipo" value={tipoFaenaId} onChange={(e) => setTipoFaenaId(e.target.value)}>
            {tiposFaena.map((t) => <option key={t.id} value={t.id}>{t.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Prioridad</FormLabel>
          <Select value={prioridad} onChange={(e) => setPrioridad(e.target.value)}>
            {PRIORIDADES.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input value={responsable} onChange={(e) => setResponsable(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            {ESTATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha programada</FormLabel>
          <Input type="date" value={fechaProgramada} onChange={(e) => setFechaProgramada(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha ejecutada</FormLabel>
          <Input type="date" value={fechaEjecutada} onChange={(e) => setFechaEjecutada(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Solicitante</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Nombre(s)</FormLabel>
          <Input value={solicitanteNombre} onChange={(e) => setSolicitanteNombre(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Apellido paterno</FormLabel>
          <Input value={solicitanteApPaterno} onChange={(e) => setSolicitanteApPaterno(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Apellido materno</FormLabel>
          <Input value={solicitanteApMaterno} onChange={(e) => setSolicitanteApMaterno(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Teléfono</FormLabel>
          <Input value={solicitanteTelefono} onChange={(e) => setSolicitanteTelefono(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Celular</FormLabel>
          <Input value={solicitanteCelular} onChange={(e) => setSolicitanteCelular(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Correo</FormLabel>
          <Input value={solicitanteCorreo} onChange={(e) => setSolicitanteCorreo(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Domicilio / Ubicación</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Estado</FormLabel>
          <Input value={estado} onChange={(e) => setEstado(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Municipio</FormLabel>
          <Input value={municipio} onChange={(e) => setMunicipio(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Colonia</FormLabel>
          <Input value={colonia} onChange={(e) => setColonia(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Código postal</FormLabel>
          <Input value={codigoPostal} onChange={(e) => setCodigoPostal(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de vialidad</FormLabel>
          <Select placeholder="Selecciona" value={tipoVialidad} onChange={(e) => setTipoVialidad(e.target.value)}>
            {TIPOS_VIALIDAD.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Vialidad (nombre)</FormLabel>
          <Input value={vialidad} onChange={(e) => setVialidad(e.target.value)} />
        </FormControl>
        <HStack>
          <FormControl>
            <FormLabel fontSize="sm">No. exterior</FormLabel>
            <Input value={numeroExterior} onChange={(e) => setNumeroExterior(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">No. interior</FormLabel>
            <Input value={numeroInterior} onChange={(e) => setNumeroInterior(e.target.value)} />
          </FormControl>
        </HStack>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Referencias</FormLabel>
          <Input value={referencias} onChange={(e) => setReferencias(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Ubicación en el mapa</FormLabel>
          <MapSelector
            initialLat={latitud ? Number(latitud) : null}
            initialLng={longitud ? Number(longitud) : null}
            onLocationSelect={(lat, lng, address) => {
              setLatitud(String(lat));
              setLongitud(String(lng));
              setUrlMaps(`https://www.google.com/maps?q=${lat},${lng}`);
              if (address?.neighborhood && !colonia) setColonia(address.neighborhood);
              if (address?.postalCode && !codigoPostal) setCodigoPostal(address.postalCode);
            }}
          />
          {latitud && longitud && (
            <Link fontSize="sm" color="blue.500" isExternal mt={1} display="inline-block"
              href={urlMaps || `https://www.google.com/maps?q=${latitud},${longitud}`}>
              Ver en Google Maps ({latitud}, {longitud})
            </Link>
          )}
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioFaena;
